import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiGet, apiPut } from "../../api/client";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useUserSession } from "../../context/UserContext";
import { Button } from "@components";

export type DriverOrderSettingsHandle = { save: () => Promise<void> };

export const DriverOrderSettings = forwardRef<
  DriverOrderSettingsHandle,
  { embedded?: boolean; disabled?: boolean }
>(function DriverOrderSettings({ embedded = false, disabled = false }, ref) {
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const workspace = activeWorkspace?.workspaceId;
  const token = userSession?.token?.accessToken;
  const { t } = useTranslation("orders");
  const [values, setValues] = useState<{
    enabled: boolean;
    allowNewCustomers: boolean;
    allowPlanning?: boolean;
    allowOptimization?: boolean;
  }>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const canEdit = ["Owner", "Administrator"].includes(
    activeWorkspace?.role ?? "",
  );
  useEffect(() => {
    let current = true;
    setValues(undefined);
    setMessage("");
    if (workspace && token && canEdit)
      apiGet(token, "/api/drivers/order-settings", workspace)
        .then((r) => {
          if (current) setValues(r.data);
        })
        .catch(() => {
          if (current) setMessage("failed");
        });
    return () => {
      current = false;
    };
  }, [workspace, token, canEdit]);
  const save = async () => {
    if (!canEdit) return;
    if (!workspace || !token || !values) {
      setMessage("failed");
      throw new Error("Driver order settings are not loaded");
    }
    setBusy(true);
    setMessage("");
    try {
      await apiPut(token, "/api/drivers/order-settings", values, workspace);
      setMessage("saved");
    } catch (error) {
      setMessage("failed");
      throw error;
    } finally {
      setBusy(false);
    }
  };
  useImperativeHandle(ref, () => ({ save }));
  if (!canEdit) return null;
  return (
    <section className="rounded-xl border border-border p-5 space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-text">
          {t("driverOrder.settingsTitle")}
        </h3>
        <p className="mt-1 text-sm text-secondary">
          {t("driverOrder.settingsDescription")}
        </p>
      </div>
      {values && (
        <>
          <label className="flex gap-3 items-start text-text">
            <input
              type="checkbox"
              className="mt-1 rounded text-lgb-primary accent-lgb-primary focus:ring-lgb-primary"
              checked={values.enabled}
              disabled={busy || disabled}
              onChange={(e) =>
                setValues({ ...values, enabled: e.target.checked })
              }
            />
            <span>{t("driverOrder.enabled")}</span>
          </label>
          <label className="flex gap-3 items-start text-text">
            <input
              type="checkbox"
              className="mt-1 rounded text-lgb-primary accent-lgb-primary focus:ring-lgb-primary"
              checked={values.allowNewCustomers}
              disabled={busy || disabled || !values.enabled}
              onChange={(e) =>
                setValues({ ...values, allowNewCustomers: e.target.checked })
              }
            />
            <span>{t("driverOrder.allowNew")}</span>
          </label>
          {(["allowPlanning", "allowOptimization"] as const).map((key) => (
            <label key={key} className="flex gap-3 items-start text-text">
              <input
                type="checkbox"
                className="mt-1 rounded text-lgb-primary accent-lgb-primary focus:ring-lgb-primary"
                checked={values[key] === true}
                disabled={
                  busy ||
                  disabled ||
                  (key === "allowOptimization" && !values.allowPlanning)
                }
                onChange={(e) =>
                  setValues({ ...values, [key]: e.target.checked })
                }
              />
              <span>{t(`driverOrder.${key}`)}</span>
            </label>
          ))}
          {!embedded && (
            <Button
              type="button"
              size="sm"
              disabled={busy || disabled}
              onClick={() => {
                void save().catch(() => {});
              }}
            >
              {t(busy ? "driverOrder.saving" : "driverOrder.saveSettings")}
            </Button>
          )}
        </>
      )}
      {message && (
        <p role="status" className="text-sm text-text">
          {t(`driverOrder.${message}`)}
        </p>
      )}
    </section>
  );
});
